import React, { useEffect, useMemo, useState } from 'react';
import { commercialConfigAPI, licenseAPI, settingsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FaSave } from 'react-icons/fa';
import { useAppSettings } from '../context/AppSettingsContext';
import { MODULE_ACCESS_CHANGED } from '../utils/moduleAccess';
import HardResetCard from '../components/HardResetCard';
import AppIntegrationsEditor from '../components/AppIntegrationsEditor';

// Self-contained — activation is its own action (its own e-sign-free POST,
// Super Admin only, already gated by this whole page), not part of the big
// "Save Configuration" button the rest of this page uses.
const LicenseSection = () => {
    const [loading, setLoading] = useState(true);
    const [activating, setActivating] = useState(false);
    const [status, setStatus] = useState(null);
    const [signingInfo, setSigningInfo] = useState(null);
    const [keyInput, setKeyInput] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [statusRes, signingRes] = await Promise.all([licenseAPI.get(), licenseAPI.getSigningKey()]);
            setStatus(statusRes?.data?.data || null);
            setSigningInfo(signingRes?.data?.data || null);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load license status');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleActivate = async () => {
        if (!keyInput.trim()) { toast.warning('Enter a license key first'); return; }
        setActivating(true);
        try {
            await licenseAPI.activate(keyInput.trim());
            toast.success('License activated');
            setKeyInput('');
            await load();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to activate license key');
        } finally {
            setActivating(false);
        }
    };

    const license = status?.license;
    const statusColor = license?.status === 'ACTIVE' && !status?.isExpired ? 'var(--success-color, #16a34a)' : 'var(--danger-color, #dc2626)';

    return (
        <div className="form-section">
            <h3>License</h3>
            <p style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '-6px' }}>
                This server's license key — signed and bound to this one installation. Activating a key here unlocks the application for every role; without a valid key the app is locked to everyone except Super Admin.
            </p>
            {loading ? (
                <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Loading license status…</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                        <div className="form-group">
                            <label className="form-label">This Server's Installation ID</label>
                            <input className="form-control" readOnly value={status?.installationId || ''} />
                            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
                                Give this to your provider — every license key is issued for one specific Installation ID and only activates here.
                            </p>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Status</label>
                            <div style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', fontWeight: 700, color: statusColor }}>
                                {license ? `${license.status}${status?.isExpired ? ' (Expired)' : ''}` : 'Not Activated'}
                            </div>
                        </div>
                    </div>

                    {license && (
                        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                            <div className="form-group"><label className="form-label">Plan</label><input className="form-control" readOnly value={license.plan_name || '—'} /></div>
                            <div className="form-group"><label className="form-label">Client</label><input className="form-control" readOnly value={license.client_name || '—'} /></div>
                            <div className="form-group"><label className="form-label">Valid Until</label><input className="form-control" readOnly value={license.valid_until ? new Date(license.valid_until).toLocaleDateString() : 'Perpetual'} /></div>
                            <div className="form-group"><label className="form-label">Active Users</label><input className="form-control" readOnly value={status?.usage?.users ?? '—'} /></div>
                            <div className="form-group"><label className="form-label">Approved Devices</label><input className="form-control" readOnly value={status?.usage?.approved_devices ?? '—'} /></div>
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">Activate / Renew License Key</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <input className="form-control" style={{ flex: 1, minWidth: '280px', fontFamily: 'monospace' }} placeholder="PCLN-ENTR-2026-CLIENT-0001-120-IXXXXXXXXXXXX-<signature>" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} />
                            <button type="button" className="btn btn-primary" onClick={handleActivate} disabled={activating}>{activating ? 'Activating…' : 'Activate'}</button>
                        </div>
                    </div>

                    {signingInfo?.vendorBuild && (
                        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                            Vendor signing key present on this machine (fingerprint {signingInfo.publicKeyFingerprint}) — keys can be minted here via <code>scripts/generateLicenseKey.js</code>.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const MODULE_OPTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'product_master', label: 'Product Master' },
  { key: 'equipment_master', label: 'Equipment Master' },
  { key: 'cleaning_config', label: 'Cleaning Config' },
  { key: 'analytical_methods', label: 'Analytical Methods' },
  { key: 'vrl', label: 'VRL' },
  { key: 'worst_case', label: 'Worst Case' },
  { key: 'maco', label: 'MACO' },
  { key: 'sampling_plans', label: 'Sampling Plans' },
  { key: 'results', label: 'Results' },
  { key: 'training', label: 'Training' },
  { key: 'ccv', label: 'CCV' },
  { key: 'revalidation', label: 'Revalidation' },
  { key: 'audit_trail', label: 'Audit Trail' },
  { key: 'user_management', label: 'User Management' },
  { key: 'documents', label: 'Documents' },
  { key: 'admin_settings', label: 'Admin Settings' },
  { key: 'ai_assistant', label: 'AI Assistant' },
  { key: 'risk_assessment', label: 'Risk Assessment' },
  { key: 'impact_assessment', label: 'Impact Assessment' },
  { key: 'acceptance_criteria', label: 'Acceptance Criteria' },
  { key: 'hold_time', label: 'Hold Time Studies (CEHT / DEHT)' }
];

const buildDefaultModules = () =>
  MODULE_OPTIONS.reduce((acc, item) => {
    acc[item.key] = true;
    return acc;
  }, {});

const CommercialSetup = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [appNameSetting, setAppNameSetting] = useState('');
    // Plans on sale. One is active for this client; any of them can be opened and edited.
    const [plans, setPlans] = useState([]);
    const [activePlanCode, setActivePlanCode] = useState('ENTERPRISE');
    const [editingPlanCode, setEditingPlanCode] = useState('ENTERPRISE');
    const { refreshAppSettings } = useAppSettings();
    const [form, setForm] = useState({
        profile: { client_name: '' },
        package: { package_code: 'ENTERPRISE', package_name: 'Enterprise', description: '' },
        limits: { max_users: '', max_lines: '', max_plants: '', max_equipment_trains: '', max_equipment: '', max_modules: '', notes: '' },
        training: { lms_sync_enabled: false }
    });

    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch (error) {
            return {};
        }
    }, []);

    const isSuperAdmin = useMemo(() => (currentUser?.role || '').toLowerCase() === 'super admin', [currentUser]);

    useEffect(() => {
        if (isSuperAdmin) {
            loadConfig();
        } else {
            setLoading(false);
        }
    }, [isSuperAdmin]);

    const loadConfig = async () => {
        setLoading(true);
        try {
            const [response, settingsResponse] = await Promise.all([
                commercialConfigAPI.getFull(),
                settingsAPI.getAll()
            ]);
            const data = response?.data?.data || {};
            const incomingModules = data.modules || {};
            setAppNameSetting(settingsResponse?.data?.data?.app_name || '');

            const normalizedModules = buildDefaultModules();
            MODULE_OPTIONS.forEach((item) => {
                if (Object.prototype.hasOwnProperty.call(incomingModules, item.key)) {
                    normalizedModules[item.key] = incomingModules[item.key] !== false;
                }
            });

            const activeCode = data.package?.package_code || 'ENTERPRISE';
            const loadedPlans = (Array.isArray(data.packages) ? data.packages : []).map((plan) => {
                const planModules = buildDefaultModules();
                MODULE_OPTIONS.forEach((item) => {
                    const source = plan.package_code === activeCode ? incomingModules : (plan.modules || {});
                    if (Object.prototype.hasOwnProperty.call(source, item.key)) planModules[item.key] = source[item.key] !== false;
                });
                return { package_code: plan.package_code, package_name: plan.package_name || plan.package_code, description: plan.description || '', modules: planModules };
            });
            setPlans(loadedPlans.length ? loadedPlans : [{ package_code: activeCode, package_name: data.package?.package_name || activeCode, description: data.package?.description || '', modules: normalizedModules }]);
            setActivePlanCode(activeCode);
            setEditingPlanCode(activeCode);

            setForm({
                profile: { client_name: data.profile?.client_name || '' },
                package: { package_code: data.package?.package_code || 'ENTERPRISE', package_name: data.package?.package_name || 'Enterprise', description: data.package?.description || '' },
                limits: { max_users: data.limits?.max_users ?? '', max_lines: data.limits?.max_lines ?? '', max_plants: data.limits?.max_plants ?? '', max_equipment_trains: data.limits?.max_equipment_trains ?? '', max_equipment: data.limits?.max_equipment ?? '', max_modules: data.limits?.max_modules ?? '', notes: data.limits?.notes || '' },
                modules: normalizedModules,
                training: { lms_sync_enabled: !!data.training?.lms_sync_enabled }
            });
        } catch (error) {
            console.error('Failed to load commercial config:', error);
            toast.error('Failed to load commercial setup');
        } finally {
            setLoading(false);
        }
    };

    const updateNested = (section, key, value) => {
        setForm((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
    };

    const updatePlan = (code, changes) => {
        setPlans((prev) => prev.map((plan) => (plan.package_code === code ? { ...plan, ...changes } : plan)));
    };
    const togglePlanModule = (code, moduleKey, enabled) => {
        setPlans((prev) => prev.map((plan) => (plan.package_code === code ? { ...plan, modules: { ...plan.modules, [moduleKey]: enabled } } : plan)));
    };

    const handleSave = async () => {
        const activePlan = plans.find((plan) => plan.package_code === activePlanCode) || plans[0];
        const payload = {
            profile: form.profile,
            package: activePlan
                ? { package_code: activePlan.package_code, package_name: activePlan.package_name, description: activePlan.description }
                : form.package,
            limits: form.limits,
            modules: activePlan ? activePlan.modules : form.modules,
            packages: plans
        };

        setSaving(true);
        try {
            await commercialConfigAPI.saveFull(payload);
            await settingsAPI.updateSetting('app_name', appNameSetting);
            await refreshAppSettings();
            window.dispatchEvent(new Event(MODULE_ACCESS_CHANGED));
            toast.success('Commercial setup saved successfully!');
            await loadConfig();
        } catch (error) {
            console.error('Save commercial config error:', error);
            toast.error(error.response?.data?.message || 'Failed to save commercial setup');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="loading-container"><div className="spinner"></div></div>;
    }

    if (!isSuperAdmin) {
        return (
            <div>
                <h2 className="page-title">Permission Denied</h2>
                <p className="page-subtitle">You do not have permission to access this page. Please contact a Super Admin.</p>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h2 className="page-title">Commercial Setup</h2>
                    <p className="page-subtitle">Client, license, limits, plans, modules and app integrations (LMS, QMS, SAP, other apps). Company details, units/sections and compliance gates are in App Configuration → Company &amp; Units.</p>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" type="button" onClick={handleSave} disabled={saving}>
                        <FaSave /> {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>
            </div>

            <div style={{ paddingTop: '0' }}>
                <div className="form-section">
                    <h3>Client Profile</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">App Name</label>
                            <input className="form-control" value={appNameSetting} onChange={(e) => setAppNameSetting(e.target.value)} placeholder="PrimeClean" />
                            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
                                Shown in the header, browser tab, login screen, and on every exported PDF/Excel document.
                            </p>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Client Name</label>
                            <input className="form-control" value={form.profile.client_name} onChange={(e) => updateNested('profile', 'client_name', e.target.value)} />
                        </div>
                    </div>
                </div>

                <LicenseSection />

                <div className="form-section">
                    <h3>Limits</h3>
                    <div className="form-grid" style={{gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'}}>
                        <div className="form-group"><label className="form-label">Max Users</label><input className="form-control" type="number" min="0" value={form.limits.max_users} onChange={(e) => updateNested('limits', 'max_users', e.target.value)} /></div>
                        <div className="form-group"><label className="form-label">Max Sections</label><input className="form-control" type="number" min="0" value={form.limits.max_lines} onChange={(e) => updateNested('limits', 'max_lines', e.target.value)} /></div>
                        <div className="form-group"><label className="form-label">Max Plants</label><input className="form-control" type="number" min="0" value={form.limits.max_plants} onChange={(e) => updateNested('limits', 'max_plants', e.target.value)} /></div>
                        <div className="form-group"><label className="form-label">Max Equipment Trains</label><input className="form-control" type="number" min="0" value={form.limits.max_equipment_trains} onChange={(e) => updateNested('limits', 'max_equipment_trains', e.target.value)} /></div>
                        <div className="form-group"><label className="form-label">Max Equipment</label><input className="form-control" type="number" min="0" value={form.limits.max_equipment} onChange={(e) => updateNested('limits', 'max_equipment', e.target.value)} /></div>
                        <div className="form-group"><label className="form-label">Max Enabled Modules</label><input className="form-control" type="number" min="0" value={form.limits.max_modules} onChange={(e) => updateNested('limits', 'max_modules', e.target.value)} /></div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Plans &amp; Modules</h3>
                    <p style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '-6px' }}>
                        Choose the plan this client has bought. A module that is off in the active plan disappears from the menu, and its buttons disappear from other pages. Every plan can be edited; changes take effect when you save.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                        {plans.map((plan) => {
                            const isActive = plan.package_code === activePlanCode;
                            const isEditing = plan.package_code === editingPlanCode;
                            const enabledCount = MODULE_OPTIONS.filter((module) => plan.modules[module.key] !== false).length;
                            return (
                                <div key={plan.package_code} onClick={() => setEditingPlanCode(plan.package_code)}
                                    style={{ cursor: 'pointer', padding: '12px 14px', borderRadius: '8px', background: 'var(--card-bg)', border: isEditing ? '2px solid var(--primary-color)' : '1px solid var(--border-color)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                        <strong style={{ fontSize: 'var(--fs-lg)' }}>{plan.package_name}</strong>
                                        {isActive && <span className="badge badge-success">Active</span>}
                                    </div>
                                    <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', margin: '4px 0 10px', minHeight: '32px' }}>{plan.description || '—'}</div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{enabledCount} of {MODULE_OPTIONS.length} modules</span>
                                        {!isActive && (
                                            <button type="button" className="btn btn-sm btn-outline" onClick={(e) => { e.stopPropagation(); setActivePlanCode(plan.package_code); setEditingPlanCode(plan.package_code); }}>
                                                Make active
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {plans.filter((plan) => plan.package_code === editingPlanCode).map((plan) => (
                        <div key={plan.package_code} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px' }}>
                            <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: '12px' }}>
                                <div className="form-group"><label className="form-label">Plan Name</label><input className="form-control" value={plan.package_name} onChange={(e) => updatePlan(plan.package_code, { package_name: e.target.value })} /></div>
                                <div className="form-group" style={{ gridColumn: 'span 2' }}><label className="form-label">Description</label><input className="form-control" value={plan.description} onChange={(e) => updatePlan(plan.package_code, { description: e.target.value })} /></div>
                            </div>
                            <label className="form-label">Modules in {plan.package_name}</label>
                            <div className="form-grid" style={{gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'}}>
                                {MODULE_OPTIONS.map((module) => (
                                    <label key={module.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', borderRadius: '8px', background: 'var(--bg-color)' }}>
                                        <input type="checkbox" checked={plan.modules[module.key] !== false} onChange={(e) => togglePlanModule(plan.package_code, module.key, e.target.checked)} style={{width: '18px', height: '18px'}}/>
                                        {module.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <AppIntegrationsEditor />
            </div>
            <HardResetCard />
        </div>
    );
};

export default CommercialSetup;

